// Live's elements (#229, `elements-editor-overlay/schema.ts`): each a frontal image, more images
// of it from other angles, and a video, which parts a model takes and needs being its block's
// `elements` (G6). An element is made in the editor and kept in the draft; the request sends
// each as `{frontal, images, video}`, and beyond live its `voice_id`
// (spec G2-kling-element-voice-picker).
import type {
  CatalogImageModel,
  CatalogVideoModel,
  Element,
  ElementCaps,
  GenerationType,
  InputRequirement,
  Voice,
} from "../../../api/generated/model";
import type { PickedImage } from "../../../platform/media/ImagePicker";

/** The voice an element speaks with: its id, and its name and kind once known (a clone's come
 * from the voices' lists). */
export interface ElementVoice {
  id: string;
  name: string | null;
  kind: Voice["kind"] | null;
}

/** An element as the composer holds it: its pictures and names come with it. */
export interface ElementDraft {
  frontal: PickedImage | null;
  images: PickedImage[];
  video: PickedImage | null;
  voice: ElementVoice | null;
}

export const NO_ELEMENT: ElementDraft = { frontal: null, images: [], video: null, voice: null };

/** The type's element caps, or null where it takes none. */
export function elementCapsOf(
  model: CatalogImageModel | CatalogVideoModel,
  type: GenerationType,
): ElementCaps | null {
  const caps = model.medium === "video" ? model.modes[type]?.elements : undefined;
  return caps === undefined || caps === null ? null : caps;
}

/** The elements a type sends: none where it takes none, no voice where its block takes none. */
export function sentElements(elements: ElementDraft[], caps: ElementCaps | null): ElementDraft[] {
  if (caps === null) return [];
  return (caps.max_voices ?? 0) > 0 ? elements : elements.map((each) => ({ ...each, voice: null }));
}

const unsupported = (part: InputRequirement) => part === "none";
const required = (part: InputRequirement) => part === "required";

/**
 * What keeps an element from being added, in live's words and order; none when it can be. The
 * last, "Upload frontal image + at least one reference image, or a driving video", is live's
 * image pair rule.
 */
export function elementProblems(element: ElementDraft, caps: ElementCaps): string[] {
  const problems: string[] = [];
  if (unsupported(caps.frontal) && element.frontal !== null) {
    problems.push("Frontal image is not supported by this model");
  }
  if (required(caps.frontal) && element.frontal === null) {
    problems.push("Frontal image is required");
  }
  if (unsupported(caps.video) && element.video !== null) {
    problems.push("Video is not supported by this model");
  }
  if (required(caps.video) && element.video === null) problems.push("Video is required");
  const count = element.images.length;
  const { min = null, max } = caps.images;
  if (max === 0 && count > 0) problems.push("Reference images are not supported by this model");
  if (min !== null && count < min) {
    problems.push(`At least ${min} reference image${min === 1 ? "" : "s"} required`);
  }
  if (count > max) problems.push(`At most ${max} reference image${max === 1 ? "" : "s"} allowed`);
  if (caps.image_pair === true) {
    const pair = element.frontal !== null && count >= 1;
    const video = element.video !== null && !unsupported(caps.video);
    if (!pair && !video) {
      problems.push("Upload frontal image + at least one reference image, or a driving video");
    }
  }
  return problems;
}

/** The element as the request sends it. */
export function elementOf(element: ElementDraft): Element {
  const ref = (image: PickedImage) => ({ kind: "asset" as const, asset_id: image.assetId });
  return {
    frontal: element.frontal === null ? null : ref(element.frontal),
    images: element.images.map(ref),
    video: element.video === null ? null : ref(element.video),
    ...(element.voice === null ? {} : { voice_id: element.voice.id }),
  };
}
